import { OtoIndividualTransferMethodEnum } from '../../enums/enrollment/oto-individual-transfer-method.enum'
import { DeepPartial } from '../../utils'
import { InsuranceProperties } from '../insurance/properties.insurance'
import { OtoIndividualPlace } from '../journey-place/oto-individual-place'
import {
  ProductOtoIndividual,
  ProductOtoIndividualJourneyPlace
} from '../product/product-oto-individual'
import { PromoCode } from '../promo-code/promo-code'
import { InsuranceEnrollmentPropositionInput } from './enrollment-proposition-input'
import { OtoIndividualProposition } from './oto-individual-proposition'
import { PropositionPricingInput } from './proposition-pricing'

export interface OtoIndividualPropositionInput {
  product: ProductOtoIndividual
  numberOfWeeks: number
  departureDate: string
  // date de retour : pré-remplie depuis la date de départ et le nombre de semaines, modifiable ;
  // absente, le backend la recalcule
  returnDate?: string
  numberOfHours: number
  // frais de séjour enregistrés par le backend ; absent tant que la proposition n'est pas
  // sauvegardée, ou après une réédition du séjour (recalculés à l'affichage)
  journeyPrice?: number
  // Région du séjour
  regionOfStay: ProductOtoIndividualJourneyPlace
  // Lieu d'arrivée
  arrivalPlace: Pick<OtoIndividualPlace, 'id' | 'title' | 'code'>
  withOption: boolean
  optionPrice?: number
  transferMethod: OtoIndividualTransferMethodEnum
  transferPrice?: number
  pricing?: PropositionPricingInput
  insurances?: InsuranceEnrollmentPropositionInput[]
  promoCode?: Pick<PromoCode, 'code' | 'discount' | 'type' | 'application'>
}

export interface OtoIndividualPropositionRequest {
  productId: number
  numberOfWeeks: number
  departureDate: string
  returnDate?: string
  numberOfHours: number
  regionOfStayId: number
  arrivalPlaceId: number
  optionId?: number
  optionPrice?: number
  transferMethod: OtoIndividualTransferMethodEnum
  transferPrice?: number
  pricing?: PropositionPricingInput
  promoCode?: string
  insuranceIds?: number[]
}

// The catalogue prices behind the proposition are served to LEC and to the prestataire only: the
// wizard can be seeded from a proposition only when they came through.
export const hasOtoIndividualCatalogue = <
  T extends { regionOfStay: DeepPartial<ProductOtoIndividualJourneyPlace> }
>(
  proposition: T
): proposition is T & { regionOfStay: ProductOtoIndividualJourneyPlace } =>
  proposition.regionOfStay.weeklyPrice !== undefined

export const mapOtoIndividualPropositionToInput = (
  proposition: OtoIndividualProposition
): OtoIndividualPropositionInput => ({
  product: {
    id: proposition.product.id,
    name: proposition.product.name,
    country: proposition.product.country,
    journeyPlaces: [proposition.regionOfStay]
  } as ProductOtoIndividual,
  numberOfWeeks: proposition.numberOfWeeks,
  departureDate: proposition.departureDate,
  returnDate: proposition.returnDate,
  numberOfHours: proposition.numberOfHours,
  journeyPrice: proposition.journeyPrice,
  regionOfStay: proposition.regionOfStay,
  arrivalPlace: proposition.arrivalPlace,
  withOption: !!proposition.option,
  optionPrice: proposition.optionPrice,
  transferMethod: proposition.transferMethod,
  transferPrice: proposition.transferPrice,
  pricing: {
    includeApplicationFees: proposition.pricing.includeApplicationFees,
    includeFamilyDiscount: proposition.pricing.includeFamilyDiscount
  },
  insurances: InsuranceProperties.mapEnrollmentInsurancesToInput(proposition.insurances),
  promoCode: proposition.promoCode
    ? {
        code: proposition.promoCode.code,
        discount: proposition.pricing.promoCodeDiscount ?? proposition.promoCode.discount,
        type: proposition.pricing.promoCodeType ?? proposition.promoCode.type,
        application: proposition.promoCode.application
      }
    : undefined
})

export const mapOtoIndividualPropositionInputToRequest = (
  input: OtoIndividualPropositionInput
): OtoIndividualPropositionRequest => ({
  productId: input.product.id,
  numberOfWeeks: input.numberOfWeeks,
  departureDate: input.departureDate,
  ...(input.returnDate && { returnDate: input.returnDate }),
  numberOfHours: input.numberOfHours,
  regionOfStayId: input.regionOfStay.id,
  arrivalPlaceId: input.arrivalPlace.id,
  ...(input.withOption && {
    optionId: input.regionOfStay.option.id,
    optionPrice: input.optionPrice ?? input.regionOfStay.optionPrice
  }),
  transferMethod: input.transferMethod,
  ...(input.transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP && {
    transferPrice: input.transferPrice ?? input.regionOfStay.place.price
  }),
  pricing: {
    includeApplicationFees: input.pricing?.includeApplicationFees ?? true,
    includeFamilyDiscount: input.pricing?.includeFamilyDiscount ?? false
  },
  ...(input.insurances !== undefined && {
    insuranceIds: input.insurances.map((insurance) => insurance.id)
  }),
  ...(input.promoCode && { promoCode: input.promoCode.code })
})
